import { useQuery, UseQueryResult } from '@tanstack/solid-query';
import type { UndefinedInitialDataOptions } from '@tanstack/solid-query';
import type { Auth } from '../generated/core/auth.gen';
import {
  getProjectsByProjectIdFeatures,
  getProjectsByProjectIdFeaturesById,
} from '../generated';
import type {
  Feature,
  FeatureCollection,
  GetProjectsByProjectIdFeaturesByIdData,
  GetProjectsByProjectIdFeaturesData,
} from '../generated';
import type { Client as FeaturesClient } from '../generated/client/types.gen';
import { createFeaturesIdInFilterClause } from '../utils/features-filter-clause';
import {
  chunkItems,
  collectFeaturePages,
  FEATURES_LIST_PAGE_SIZE,
} from './features-api-list';

// eslint-disable-next-line no-magic-numbers -- Cache a feature query for five minutes across panel/model consumers.
const FEATURE_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

const BEARER_HTTP_SECURITY: readonly Auth[] = [
  { scheme: 'bearer', type: 'http' },
];

interface FeaturesApiServiceErrorContext {
  readonly request?: Request;
  readonly response?: Response;
  readonly error?: unknown;
}

/** Failed Features API call. Preserves response headers for support (e.g. tcp-request-id). */
export class FeaturesApiServiceError extends Error {
  public readonly request?: Request;
  public readonly response?: Response;
  public readonly responseHeaders: Readonly<Record<string, string>>;
  public readonly originalError?: unknown;

  constructor(message: string, context: FeaturesApiServiceErrorContext) {
    super(message);
    this.name = 'FeaturesApiServiceError';
    this.request = context.request;
    this.response = context.response;
    this.responseHeaders = context.response
      ? headersToRecord(context.response.headers)
      : {};
    this.originalError = context.error;
    Object.setPrototypeOf(this, FeaturesApiServiceError.prototype);
  }
}

const headersToRecord = (headers: Headers): Record<string, string> => {
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

type ProjectIdAccessor = () => string;
type FeatureIdAccessor = () => string;
export type GetFeatureByIdQuery = NonNullable<
  GetProjectsByProjectIdFeaturesByIdData['query']
>;
export type GetFeaturesQuery = NonNullable<
  GetProjectsByProjectIdFeaturesData['query']
>;
type GetFeatureByIdQueryKey = [
  'features-api-service',
  'get-feature-by-id',
  string,
  string,
  GetFeatureByIdQuery,
  string,
];
type GetFeaturesByIdsQueryKey = [
  'features-api-service',
  'get-features-by-ids',
  string,
  readonly string[],
  GetFeaturesQuery,
  string,
];
type GetFeatureByIdQueryOptions = ReturnType<
  UndefinedInitialDataOptions<
    FeatureCollection,
    Error,
    FeatureCollection,
    GetFeatureByIdQueryKey
  >
>;
type GetFeaturesByIdsQueryOptions = ReturnType<
  UndefinedInitialDataOptions<
    FeatureCollection,
    Error,
    FeatureCollection,
    GetFeaturesByIdsQueryKey
  >
>;

export interface FeaturesApiServiceOptions {
  client(): FeaturesClient;
  authToken(): string;
  /** Distinguishes TanStack Query cache entries across region/env (defaults to `default`). */
  queryScope?(): string;
}

/**
 * Typed Features API reads. Passes bearer `security` because the generated SDK
 * has no OpenAPI security entries. Prefer `fetchFeaturesByIds` over per-id GET
 * when loading many features.
 */
export class FeaturesApiService {
  constructor(private readonly options: FeaturesApiServiceOptions) {}

  /** Solid Query hook for `GET /projects/{projectId}/features/{id}`. */
  public getFeatureById(
    projectId: ProjectIdAccessor,
    featureId: FeatureIdAccessor,
    query: GetFeatureByIdQuery = {}
  ): UseQueryResult<FeatureCollection, Error> {
    return useQuery(() =>
      this.getFeatureByIdQueryOptions(projectId(), featureId(), query)
    );
  }

  /** Query options for `GET /projects/{projectId}/features/{id}`. */
  public getFeatureByIdQueryOptions(
    projectId: string,
    featureId: string,
    query: GetFeatureByIdQuery = {}
  ): GetFeatureByIdQueryOptions {
    return {
      queryKey: [
        'features-api-service',
        'get-feature-by-id',
        projectId,
        featureId,
        query,
        this.getQueryScope(),
      ],
      queryFn: ({ signal }) =>
        fetchFeatureById(
          projectId,
          featureId,
          query,
          this.getClient(),
          this.getAuthToken(),
          signal
        ),
      staleTime: FEATURE_QUERY_STALE_TIME_MS,
    };
  }

  /**
   * Query options for batched `GET /projects/{projectId}/features` by id.
   * Feature ids are sorted in the query key so cache hits do not depend on order.
   */
  public getFeaturesByIdsQueryOptions(
    projectId: string,
    featureIds: readonly string[],
    query: GetFeaturesQuery = {}
  ): GetFeaturesByIdsQueryOptions {
    const sortedFeatureIds = [...featureIds].sort();

    return {
      queryKey: [
        'features-api-service',
        'get-features-by-ids',
        projectId,
        sortedFeatureIds,
        query,
        this.getQueryScope(),
      ],
      queryFn: ({ signal }) =>
        this.fetchFeaturesByIds(projectId, sortedFeatureIds, query, signal),
      staleTime: FEATURE_QUERY_STALE_TIME_MS,
    };
  }

  /** One-shot `GET /projects/{projectId}/features/{id}`. */
  public fetchFeatureById(
    projectId: string,
    featureId: string,
    query: GetFeatureByIdQuery = {},
    signal?: AbortSignal
  ): Promise<FeatureCollection> {
    return fetchFeatureById(
      projectId,
      featureId,
      query,
      this.getClient(),
      this.getAuthToken(),
      signal
    );
  }

  /** One-shot `GET /projects/{projectId}/features` with optional filter/paging query. */
  public fetchFeatures(
    projectId: string,
    query: GetFeaturesQuery = {},
    signal?: AbortSignal
  ): Promise<FeatureCollection> {
    return fetchFeatures(
      projectId,
      query,
      this.getClient(),
      this.getAuthToken(),
      signal
    );
  }

  /**
   * Loads features by id via `GET /projects/{projectId}/features`.
   * Chunks ids into pages of {@link FEATURES_LIST_PAGE_SIZE} with an `IN` filter
   * on `id` (keeps UUID query strings under typical 8KB gateway limits), then
   * pages each batch until `FeatureCollection.total` is collected.
   */
  public async fetchFeaturesByIds(
    projectId: string,
    featureIds: readonly string[],
    query: GetFeaturesQuery = {},
    signal?: AbortSignal
  ): Promise<FeatureCollection> {
    if (featureIds.length === 0) {
      return { features: [] };
    }

    const features: Feature[] = [];
    for (const idBatch of chunkItems(featureIds, FEATURES_LIST_PAGE_SIZE)) {
      const filter = createFeaturesIdInFilterClause(idBatch);
      const batchFeatures = await collectFeaturePages((start, size) =>
        this.fetchFeatures(
          projectId,
          {
            ...query,
            filter,
            start,
            size,
          },
          signal
        )
      );
      features.push(...batchFeatures);
    }

    return { type: 'FeatureCollection', features: [...features] };
  }

  private getClient(): FeaturesClient {
    return this.options.client();
  }

  private getAuthToken(): string {
    return this.options.authToken();
  }

  private getQueryScope(): string {
    return this.options.queryScope?.() ?? 'default';
  }
}

const fetchFeatureById = async (
  projectId: string,
  featureId: string,
  query: GetFeatureByIdQuery,
  client: FeaturesClient,
  authToken: string,
  signal?: AbortSignal
): Promise<FeatureCollection> => {
  const result = await getProjectsByProjectIdFeaturesById({
    path: { projectId, id: featureId },
    query,
    signal,
    client,
    auth: authToken,
    security: BEARER_HTTP_SECURITY,
  });
  const { data } = result;

  if (data == null) {
    throw new FeaturesApiServiceError('Failed to fetch feature by id', result);
  }

  return data;
};

const fetchFeatures = async (
  projectId: string,
  query: GetFeaturesQuery,
  client: FeaturesClient,
  authToken: string,
  signal?: AbortSignal
): Promise<FeatureCollection> => {
  const result = await getProjectsByProjectIdFeatures({
    path: { projectId },
    query,
    signal,
    client,
    auth: authToken,
    security: BEARER_HTTP_SECURITY,
  });
  const { data } = result;

  if (data == null) {
    throw new FeaturesApiServiceError('Failed to fetch features', result);
  }

  return data;
};
